import DOMPurify from "dompurify";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpRight, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, Product } from "../lib/api";
import { money } from "../lib/format";
import { shopLinks } from "../lib/links";
import { useBasket } from "../context/BasketContext";
import { usePick } from "../components/PickMode";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { MyReview, Review, ReviewForm, ReviewList, Stars } from "../components/Reviews";

export default function ProductPage() {
  const { handle } = useParams();
  const { add } = useBasket();
  const pick = usePick();
  const { data: product, isLoading } = useQuery<Product>({ queryKey: ["product", handle], queryFn: () => api.get(`/catalog/products/${handle}`).then((r) => r.data) });
  const { data: rev } = useQuery<{ reviews: Review[]; rating: number; count: number; can_review: boolean; my_review: MyReview | null; shop: { rating?: number; totalReviews?: number } }>({ queryKey: ["product-reviews", handle], queryFn: () => api.get(`/reviews/product/${handle}`).then((r) => r.data), enabled: !!handle });
  const [variantId, setVariantId] = useState<string>("");
  const [qty, setQty] = useState(1);
  const [image, setImage] = useState<string | null>(null);

  useEffect(() => {
    if (product) {
      setVariantId((product.variants.find((v) => v.available) || product.variants[0])?.id || "");
      setImage(product.image);
    }
  }, [product]);

  if (isLoading || !product) return <Skeleton className="h-96 rounded-none bg-[var(--parchment)]" />;
  const variant = product.variants.find((v) => v.id === variantId) || product.variants[0];

  const addToCase = () => {
    add({ product_handle: product.handle, variant_id: variant.id, title: product.title, variant_title: variant.title, image: product.image, unit_price_pence: variant.price_pence }, qty);
    toast.success(`${qty} × ${product.title} added to your case`);
  };

  return (
    <div data-testid="product-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-neutral-600 transition-colors duration-200 hover:text-[var(--wine)]" data-testid="product-back-link"><ArrowLeft className="h-4 w-4" /> Back to wines</Link>
        {handle && <a href={shopLinks.product(handle)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-neutral-500 underline underline-offset-4 transition-colors duration-200 hover:text-[var(--wine)]" data-testid="product-shopify-link">View on hourlierwines.co.uk <ArrowUpRight className="h-3.5 w-3.5" /></a>}
      </div>
      <div className="mt-8 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <div className="aspect-[4/5] overflow-hidden bg-[var(--parchment)]">{image && <img src={image} alt={product.title} className="h-full w-full object-cover" data-testid="product-image" />}</div>
          {product.images.length > 1 && (
            <div className="mt-3 flex gap-3">
              {product.images.map((src) => (
                <button key={src} onClick={() => setImage(src)} className={`h-20 w-16 overflow-hidden border ${image === src ? "border-[var(--ink)]" : "border-border"}`}><img src={src} alt="" className="h-full w-full object-cover" /></button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-6 lg:pl-8">
          <div className="eyebrow">{product.vendor}{product.product_type ? ` · ${product.product_type}` : ""}</div>
          <h1 className="mt-3 font-serif text-4xl font-medium leading-tight tracking-tight sm:text-5xl" data-testid="product-title">{product.title}</h1>
          {rev && rev.count > 0 && <a href="#reviews" className="mt-3 inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-[var(--wine)]" data-testid="product-rating"><Stars rating={rev.rating} /> <span className="tabular-nums">{rev.rating}</span> · {rev.count} review{rev.count === 1 ? "" : "s"}</a>}
          <div className="mt-6 font-serif text-3xl tabular-nums" data-testid="product-price">{money(variant?.price_pence || product.price_min_pence)}</div>
          {product.variants.length > 1 && (
            <div className="mt-8">
              <div className="eyebrow">Format</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {product.variants.map((v) => (
                  <button key={v.id} disabled={!v.available} onClick={() => setVariantId(v.id)} className={`border px-4 py-2 text-sm transition-colors duration-200 disabled:line-through disabled:opacity-40 ${variantId === v.id ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-border hover:border-[var(--ink)]"}`} data-testid={`variant-${v.id}`}>{v.title}</button>
                ))}
              </div>
            </div>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="flex items-center border border-border">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-[var(--parchment)]" data-testid="qty-dec"><Minus className="h-4 w-4" /></button>
              <span className="w-10 text-center tabular-nums" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(Math.min(60, qty + 1))} className="p-3 hover:bg-[var(--parchment)]" data-testid="qty-inc"><Plus className="h-4 w-4" /></button>
            </div>
            {pick.userId ? (
              <Button onClick={() => variant && pick.add(product, variant, qty)} disabled={!variant} className="rounded-none bg-[var(--wine)] px-10 py-6 text-base text-[var(--paper)] hover-lift hover:bg-[var(--ink)]" data-testid="pick-add-product">
                {`Add ${qty} to ${pick.memberName}'s case · ${money((variant?.price_pence || 0) * qty)}`}
              </Button>
            ) : (
              <Button onClick={addToCase} disabled={!variant?.available} className="rounded-none px-10 py-6 text-base hover-lift" data-testid="add-to-basket-button">
                {variant?.available ? `Add to case · ${money((variant?.price_pence || 0) * qty)}` : (product.tags || []).includes("coming-soon") ? "Coming soon — new vintage on its way" : "Sold out"}
              </Button>
            )}
          </div>
          <p className="mt-3 text-xs text-neutral-500">Paid from your cellar balance at checkout. No card needed.</p>
          <div className="prose-wine mt-10 border-t border-border pt-8 text-sm text-neutral-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description_html || "", { USE_PROFILES: { html: true } }) }} data-testid="product-description" />
          {product.tags.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {product.tags.filter((t) => t && t !== "Alcohol").slice(0, 8).map((t) => <span key={t} className="border border-border px-2 py-1 text-[10px] uppercase tracking-widest text-neutral-500">{t}</span>)}
            </div>
          )}
        </div>
      </div>
      <section id="reviews" className="mt-16 border-t border-border pt-10" data-testid="product-reviews">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="eyebrow">Reviews</div>
            <h2 className="mt-2 font-serif text-2xl sm:text-3xl">What members and customers say</h2>
          </div>
          {rev?.shop?.totalReviews ? <div className="text-xs text-neutral-500" data-testid="shop-rating">Hourlier Wines overall <Stars rating={rev.shop.rating || 0} size="h-3 w-3" /> {rev.shop.rating} from {rev.shop.totalReviews} reviews</div> : null}
        </div>
        <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_380px]">
          <div>{rev ? <ReviewList reviews={rev.reviews} testId="product-review-list" /> : <Skeleton className="h-24" />}</div>
          <div>
            {rev?.can_review ? <ReviewForm handle={handle!} existing={rev.my_review} /> : (
              <div className="border border-dashed border-border p-5 text-sm text-neutral-500" data-testid="review-locked">Rate this wine once you've bought it through your cellar — verified reviews help us choose the next vintages.</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
